import React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import classnames from '../../utils/classnames.tsx';
import { withIcon } from '../Icon.tsx';
import { faCheck, faChevronDown, faCircleNotch, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { useControllableState } from '../../utils/useControllableState.tsx';
import Field, { FieldProps } from './Field.tsx';
import { mergeRefs } from '../../utils/mergeRefs.tsx';

export interface Option {
    label: string;
    value: string;
    role?: string;
    description?: string;
    disabled?: boolean;
    group?: string;
    [key: string]: any;
}

export interface OptionGroup {
    label: string;
    options: Option[];
}

interface SelectProps
    extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'value' | 'defaultValue'>,
        FieldProps {
    options: Option[];
    optionGroups?: OptionGroup[];
    renderValue?: (value: string, selectedOption?: Option) => React.ReactNode;
    renderOption?: (option: Option, value: string) => React.ReactNode;
    emptyMessage?: string;
    isLoading?: boolean;
    loadingPlaceholder?: string;
    disabled?: boolean;
    required?: boolean;
    placeholder?: string;
    readOnly?: boolean;
    modal?: boolean;
}

const ClearIcon = withIcon(faXmark);
const CheckIcon = withIcon(faCheck);
const ChevronDownIcon = withIcon(faChevronDown);
const LoadingCircleIcon = withIcon(faCircleNotch);

const defaultRenderValue = (value: string, selectedOption?: Option) => selectedOption?.label || value;

const defaultRenderOption = (option: Option) => (
    <span>
        <span className="text-content-strong">{option.label}</span>
        {option.label !== option.value ? <span className="block text-xs text-content-weak">{option.value}</span> : null}
        {option.description ? <span className="block text-xs text-content-weak">{option.description}</span> : null}
    </span>
);

const NonModalSelectField = ({
    ref,
    defaultValue,
    value: propsValue,
    onValueChange,
    onChange,
    onFocus,
    onBlur,
    onCommit,
    readOnly,
    disabled,
    required,
    controls,
    className,
    inputRef = undefined,
    options = [],
    optionGroups,
    renderValue = defaultRenderValue,
    renderOption = defaultRenderOption,
    isLoading = false,
    loadingPlaceholder,
    modal = false,
    ...props
}: SelectProps) => {
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const [value, setValue] = useControllableState(defaultValue, propsValue, (value) => {
        onValueChange?.(value);
        // @ts-ignore
        onChange?.({ target: { value } });
    });

    // Find selected option in either flat options or grouped options
    const allOptions = optionGroups ? optionGroups.flatMap((group) => group.options) : options;
    const selectedOption = allOptions.find((option) => option.value === value);

    const handleSelectOption = (option: Option) => {
        if (!option.disabled) {
            setValue(option.value);
        }
        // force mouseout to close confidence explanation
        triggerRef.current?.dispatchEvent(new MouseEvent('mouseout', { bubbles: true }));
        if (!option.disabled) {
            onCommit?.(option.value);
        }
    };

    const handleTriggerBlur = (e: any) => {
        onBlur?.(e);
        onCommit?.(value);
    };

    const handleClear = () => {
        setValue('');
        // simulate pointer down event to open dropdown - update is saved when dropdown is closed
        triggerRef.current?.dispatchEvent(
            new PointerEvent('pointerdown', {
                bubbles: true,
                cancelable: true,
                button: 0,
            })
        );
    };

    const handlePointerDownOutside = (event) => {
        // Prevent closing if the click is on the anchor element
        if (triggerRef.current && triggerRef.current.contains(event.target)) {
            event.preventDefault();
        } else {
            // refocus the trigger element to prevent focus loss
            setTimeout(() => {
                triggerRef.current?.focus();
            }, 0);
        }
    };

    return (
        <DropdownMenu.Root modal={modal}>
            <Field
                className={classnames('shadow-control group', className)}
                readOnly={readOnly}
                disabled={disabled}
                ref={ref}
            >
                <Field.Input>
                    <DropdownMenu.Trigger
                        className="px-2 py-1.5 flex-1 min-w-0 justify-between items-center flex bg-surface-transparent text-left min-h-8 relative"
                        disabled={disabled || readOnly}
                        {...props}
                        onClick={(e) => onFocus?.(null)}
                        onFocus={onFocus}
                        onBlur={handleTriggerBlur}
                        ref={mergeRefs(inputRef, triggerRef)}
                    >
                        {isLoading ? (
                            <span className="text-content-weak">{loadingPlaceholder || props.placeholder}</span>
                        ) : value !== null && value !== undefined ? (
                            renderValue(value, selectedOption)
                        ) : (
                            <span className="text-content-weak">{props.placeholder}</span>
                        )}
                    </DropdownMenu.Trigger>
                </Field.Input>
                <Field.Controls>
                    {!readOnly && !disabled && (
                        <Field.ControlButton className="group-hover:opacity-100 point !bg-surface-transparent">
                            {isLoading ? (
                                <LoadingCircleIcon spin />
                            ) : (
                                <ChevronDownIcon
                                    onClick={() => {
                                        // trigger on pointer down on trigger ref
                                        if (triggerRef.current) {
                                            onFocus?.(null);
                                            triggerRef.current.dispatchEvent(
                                                new PointerEvent('pointerdown', { bubbles: true })
                                            );
                                        }
                                    }}
                                />
                            )}
                        </Field.ControlButton>
                    )}
                    {!required && !readOnly && !disabled && value && (
                        <Field.ControlButton onClick={handleClear}>
                            <ClearIcon />
                        </Field.ControlButton>
                    )}
                    {controls}
                </Field.Controls>
            </Field>

            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    className="p-1 z-[60000] w-full max-h-96 rounded-lg border border-line-weak bg-surface-weak shadow-lg outline-none animate-in fade-in-0 zoom-in-95"
                    align="start"
                    sideOffset={5}
                    onWheel={(e) => e.stopPropagation()}
                    onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    onFocusOutside={(e) => {
                        e.preventDefault();
                    }}
                    onPointerDownOutside={handlePointerDownOutside}
                >
                    <div className="max-h-[300px] overflow-y-auto overflow-x-hidden">
                        {optionGroups
                            ? // Render grouped options
                              optionGroups.map((group, groupIndex) => (
                                  <React.Fragment key={groupIndex}>
                                      <div className="px-4 py-2 text-xs font-semibold text-content-medium bg-surface-medium rounded my-1 first:mt-0 sticky top-0 z-10">
                                          {group.label}
                                      </div>
                                      {group.options.map((option: Option, i: number) => (
                                          <DropdownMenu.Item
                                              key={`${groupIndex}-${i}`}
                                              onSelect={() => handleSelectOption(option)}
                                              disabled={option.disabled}
                                              className={classnames(
                                                  'flex items-center gap-2 w-full text-content-strong',
                                                  'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pr-8 pl-4 text-sm outline-none hover:bg-surface-strong focus:bg-surface-strong',
                                                  option.disabled && 'opacity-50',
                                                  value === option.value && 'bg-surface-brand-medium-default'
                                              )}
                                          >
                                              {renderOption(option, value)}
                                              {value === option.value && (
                                                  <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center text-content-strong">
                                                      <CheckIcon className="h-4 w-4" />
                                                  </span>
                                              )}
                                          </DropdownMenu.Item>
                                      ))}
                                  </React.Fragment>
                              ))
                            : // Render flat options
                              options.map((option: Option, i: number) => (
                                  <DropdownMenu.Item
                                      key={i}
                                      onSelect={() => handleSelectOption(option)}
                                      disabled={option.disabled}
                                      className={classnames(
                                          'flex items-center gap-2 w-full text-content-strong',
                                          'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pr-8 pl-4 text-sm outline-none hover:bg-surface-strong focus:bg-surface-strong',
                                          option.disabled && 'opacity-50',
                                          value === option.value && 'bg-surface-brand-medium-default'
                                      )}
                                  >
                                      {renderOption(option, value)}
                                      {value === option.value && (
                                          <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center text-content-strong">
                                              <CheckIcon className="h-4 w-4" />
                                          </span>
                                      )}
                                  </DropdownMenu.Item>
                              ))}
                    </div>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
};

export default NonModalSelectField;
